import type { ApiErrorResponse } from '../types';

export function extractApiError(error: any): {
  title: string;
  message: string;
  key?: string;
} {
  if (error?.response?.data) {
    const errorData = error.response.data as ApiErrorResponse;

    if (errorData.title && errorData.message) {
      return {
        title: errorData.title,
        message: errorData.message,
        key: errorData.key,
      };
    }

    if (typeof errorData === 'string') {
      return {
        title: 'Error',
        message: errorData,
      };
    }

    if (errorData.message) {
      return {
        title: errorData.title || 'Error',
        message: errorData.message,
      };
    }
  }

  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return {
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
    };
  }

  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      title: 'Timeout Error',
      message: 'Request timed out. Please try again.',
    };
  }

  if (error?.response?.status) {
    const status = error.response.status;
    switch (status) {
      case 400:
        return {
          title: 'Bad Request',
          message: 'Invalid request. Please check your input and try again.',
        };
      case 401:
        return {
          title: 'Unauthorized',
          message: 'Your session has expired. Please log in again.',
        };
      case 403:
        return {
          title: 'Forbidden',
          message: 'You do not have permission to perform this action.',
        };
      case 404:
        return {
          title: 'Not Found',
          message: 'The requested resource was not found.',
        };
      case 405:
        return {
          title: 'Method Not Allowed',
          message: 'The requested method is not allowed for the resource.',
        };
      case 409:
        return {
          title: 'Conflict',
          message: 'A conflict occurred. The resource may already exist.',
        };
      case 422:
        return {
          title: 'Validation Error',
          message: 'Please check your input and try again.',
        };
      case 429:
        return {
          title: 'Too Many Requests',
          message: 'Too many requests. Please wait a moment and try again.',
        };
      case 500:
        return {
          title: 'Server Error',
          message: 'A server error occurred. Please try again later.',
        };
      case 502:
        return {
          title: 'Bad Gateway',
          message: 'Server is temporarily unavailable. Please try again later.',
        };
      case 503:
        return {
          title: 'Service Unavailable',
          message: 'Service is temporarily unavailable. Please try again later.',
        };
      case 504:
        return {
          title: 'Gateway Timeout',
          message: 'Request timed out. Please try again.',
        };
      default:
        return {
          title: 'Error',
          message: `An error occurred (${status}). Please try again.`,
        };
    }
  }

  return {
    title: 'Error',
    message: error?.message || 'An unexpected error occurred. Please try again.',
  };
}

export function createStandardError(error: any): Error {
  const errorInfo = extractApiError(error);
  const standardError = new Error(errorInfo.message);

  return standardError;
}
